import type React from "react";
import { useMemo } from "react";
import { tv } from "tailwind-variants";
import { Sidebar } from "@/components";
import type {
  WorkflowInstance,
  WorkflowStepInstance,
} from "@/types/WorkflowInstance";
import { Flex, SkeletonLoader } from "@bdo/cornerstone-ui";
import { setSelectedStepInstanceId } from "../workflowInstanceSlice";
import { useAppDispatch } from "@/hooks";
import { WorkflowSidebarHeader } from "./WorkflowSidebarHeader";
import { WorkflowSidebarSectionTitle } from "./WorkflowSidebarSectionTitle";
import { STEP_STATUS, SKELETON_COUNT } from "./constants";
import { WorkflowSidebarSectionStep } from "./WorkflowSidebarSectionStep";

const styles = tv({
  slots: {
    activityContainer:
      "border-l border-mono-lighter ml-[10px] pl-md flex flex-col gap-xs",
  },
});

const { activityContainer: activityContainerStyle } = styles();

interface WorkflowSidebarProps {
  workflow?: WorkflowInstance;
  isLoading: boolean;
  sidebarWidth?: number;
  onResizeStart?: (e: React.MouseEvent) => void;
  onResizeDoubleClick?: () => void;
}

export function WorkflowSidebar({
  workflow,
  isLoading,
  sidebarWidth,
  onResizeStart,
  onResizeDoubleClick,
}: Readonly<WorkflowSidebarProps>) {
  const dispatch = useAppDispatch();

  const sections = useMemo(() => {
    if (!workflow) {
      return [];
    }

    return workflow.workflowStepInstances;
  }, [workflow]);

  // Groups sections by their headings. E.g. Heading 1 -> [Step 1, Step 2], Heading 2 -> [Step 3]
  const instancesDictionary: Record<string, WorkflowStepInstance[]> = useMemo(
    () =>
      sections?.reduce(
        (acc, section) => {
          if (!acc[section.heading]) {
            acc[section.heading] = [];
          }
          acc[section.heading].push(section);

          return acc;
        },
        {} as Record<string, WorkflowStepInstance[]>,
      ),
    [sections],
  );

  function handleSelectStepInstance(stepInstance: WorkflowStepInstance) {
    if (
      stepInstance.status === STEP_STATUS.COMPLETED ||
      stepInstance.status === STEP_STATUS.IN_PROGRESS
    ) {
      dispatch(setSelectedStepInstanceId(stepInstance.id));
    }
  }

  return (
    <Sidebar
      width={sidebarWidth}
      onResizeStart={onResizeStart}
      onResizeDoubleClick={onResizeDoubleClick}
    >
      <Flex direction="column">
        <WorkflowSidebarHeader workflow={workflow} isLoading={isLoading} />

        <Flex direction="column">
          {isLoading ? (
            <div className="flex flex-col gap-md">
              {Array.from({ length: SKELETON_COUNT }, (_, index) => (
                <SkeletonLoader key={index} height="24.5px" width="150px" />
              ))}
            </div>
          ) : (
            Object.entries(instancesDictionary).map(([heading, steps]) => {
              return (
                <div key={heading} className="mb-md">
                  <WorkflowSidebarSectionTitle
                    heading={heading}
                    steps={steps}
                  />
                  <div className={activityContainerStyle()}>
                    {steps.map((step) => (
                      <WorkflowSidebarSectionStep
                        key={step.id}
                        step={step}
                        onSelect={handleSelectStepInstance}
                      />
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </Flex>
      </Flex>
    </Sidebar>
  );
}
